import type { Subscription } from '@salonomy/types';
import { Button, Modal } from '@salonomy/ui';
import { env } from '../../lib/env.js';
import { useCancelSubscription } from '../../data/billing.js';
import { useToast } from '../../lib/toast.js';
import { billingDate } from './format.js';

/**
 * ONE-CLICK CANCEL (brief 10 §4; ADR-0011 §4). This is the literal proof of the FOUNDATION promise, and
 * it is screenshotted in marketing — so it must be EXACTLY one click plus one confirmation:
 *
 *   - NO retention flow, NO discount-to-stay, NO "chat to cancel", NO reason field. There is nothing to
 *     fill in. The confirm button posts `{ confirm: true }` and that's the whole interaction.
 *   - Effective END OF PERIOD, not immediate — the salon keeps everything it paid for until then.
 *   - Offers the GDPR data export on the way out (the server's `exportUrl`); data is NEVER deleted on
 *     cancel — deletion only by an explicit GDPR request.
 *   - Double-submit is guarded by the mutation's pending state; a real API error is surfaced faithfully.
 *
 * After a successful cancel the dialog shows the effective date + the export link, then `onClose` returns
 * to the panel which now offers the equally-trivial reactivate.
 */
export function CancelDialog({
  open,
  onClose,
  subscription,
}: {
  open: boolean;
  onClose: () => void;
  subscription: Subscription;
}) {
  const toast = useToast();
  const cancel = useCancelSubscription();
  const result = cancel.data;

  function close() {
    cancel.reset();
    onClose();
  }

  async function confirmCancel() {
    try {
      await cancel.mutateAsync({ confirm: true });
      // Stay open to show the effective date + export offer; the toast confirms the action landed.
      toast.success('Subscription set to cancel at the end of your period.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not cancel — please try again.');
    }
  }

  // Root the server's relative export path against the API origin so the link works in a prod build too.
  const exportHref = result ? `${env.apiBaseUrl}${result.exportUrl}` : null;
  const effectiveEnd =
    result?.subscription.currentPeriodEnd ?? subscription.currentPeriodEnd ?? null;

  return (
    <Modal
      open={open}
      onClose={close}
      title={result ? 'Cancellation scheduled' : 'Cancel subscription'}
      width={480}
      footer={
        result ? (
          <Button onClick={close}>Done</Button>
        ) : (
          <>
            <Button variant="outline" onClick={close} disabled={cancel.isPending}>
              Keep my subscription
            </Button>
            <Button variant="danger" onClick={confirmCancel} disabled={cancel.isPending}>
              {cancel.isPending ? 'Cancelling…' : 'Cancel subscription'}
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div style={{ fontSize: 13.5, lineHeight: 1.55 }}>
          <p style={{ margin: '0 0 12px' }}>
            Done — your subscription will end on <strong>{billingDate(effectiveEnd)}</strong>. Until
            then everything keeps working exactly as now. We won’t bill you again, and we’ll never
            delete your data for cancelling.
          </p>
          {exportHref ? (
            <p style={{ margin: 0 }}>
              Want a copy of your data on the way out?{' '}
              <a href={exportHref} target="_blank" rel="noopener noreferrer">
                Export everything
              </a>{' '}
              — a full, machine-readable bundle. You can also do this any time from Privacy &amp;
              data.
            </p>
          ) : null}
        </div>
      ) : (
        <div style={{ fontSize: 13.5, lineHeight: 1.55 }}>
          <p style={{ margin: '0 0 10px' }}>
            That’s it — one click. Your subscription stays active until the end of your current paid
            period, then stops. No notice period, no cancellation fee, no “are you sure” maze.
          </p>
          <p style={{ margin: '0 0 10px' }}>
            {subscription.currentPeriodEnd ? (
              <>
                You’ll keep full access until{' '}
                <strong>{billingDate(subscription.currentPeriodEnd)}</strong>.
              </>
            ) : (
              <>You’ll keep full access until the end of your current period.</>
            )}
          </p>
          <p style={{ margin: 0 }}>
            Your data is never deleted for cancelling — we’ll offer you a full export on the next
            screen, and you can come back and reactivate any time before your period ends.
          </p>
        </div>
      )}
    </Modal>
  );
}
